import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminDashboard from "./AdminDashboard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function SuperadminDashboard() {
  return (
    <div data-testid="superadmin-dashboard">
      <Tabs defaultValue="admin">
        <div className="bg-white border-b border-slate-200 sticky top-16 z-10">
          <div className="nc-container">
            <TabsList className="bg-transparent border-0 my-2">
              <TabsTrigger value="admin">Admin</TabsTrigger>
              <TabsTrigger value="admins">Admins</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
              <TabsTrigger value="audit">Audit Logs</TabsTrigger>
              <TabsTrigger value="emails">Email Logs</TabsTrigger>
            </TabsList>
          </div>
        </div>
        <TabsContent value="admin"><AdminDashboard /></TabsContent>
        <TabsContent value="admins"><AdminsTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
        <TabsContent value="emails"><EmailLogsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function AdminsTab() {
  const [admins, setAdmins] = useState([]);
  const [busy, setBusy] = useState(null);

  const refresh = () => { api.get("/superadmin/admins").then(({ data }) => setAdmins(data)); };
  useEffect(() => { refresh(); }, []);

  const toggle = async (a, key, value) => {
    setBusy(a.id);
    try {
      await api.post(`/superadmin/admins/${a.id}/permissions`, { permissions: { [key]: value } });
      refresh();
    } catch (err) {
      window.alert(err.response?.data?.detail || err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-4 bg-white rounded-2xl border border-slate-200 p-5" data-testid="admins-tab">
      <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Admin users</h2>
      <p className="text-sm text-slate-500 mt-1">
        Grant the lead-management permission to admins you trust with closing and
        deleting leads. Superadmins always have it.
      </p>
      <div className="mt-4 space-y-2">
        {admins.map(a => (
          <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 border border-slate-200 rounded-xl p-3"
               data-testid={`admin-row-${a.id}`}>
            <div>
              <div className="font-medium text-slate-900">{a.name || "—"}</div>
              <div className="text-xs text-slate-500">{a.mobile} · {a.role}</div>
            </div>
            {a.role === "superadmin" ? (
              <span className="text-xs text-slate-500">All permissions</span>
            ) : (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" disabled={busy === a.id}
                       checked={!!a.permissions?.leads_manage}
                       onChange={e => toggle(a, "leads_manage", e.target.checked)}
                       data-testid={`perm-leads-manage-${a.id}`} />
                Manage leads (close, delete, void entries)
              </label>
            )}
          </div>
        ))}
        {admins.length === 0 && <p className="text-sm text-slate-400">No admin users.</p>}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [pricing, setPricing] = useState(null);
  const [profOptions, setProfOptions] = useState([]);
  const [newProf, setNewProf] = useState("");
  const [flags, setFlags] = useState({});
  const [stages, setStages] = useState([]);
  const [newStage, setNewStage] = useState({ name: "", sort_order: "" });

  const refresh = () => {
    api.get("/pricing").then(({ data }) => setPricing(data));
    api.get("/professions").then(({ data }) => setProfOptions(data));
    api.get("/feature-flags").then(({ data }) => setFlags(data));
    api.get("/construction-stages?include_inactive=true").then(({ data }) => setStages(data));
  };
  useEffect(refresh, []);
  const saveFlags = async () => {
    await api.put("/settings", { key: "feature_flags", value: flags });
    alert("Flags saved");
  };
  const addProf = async () => {
    if (!newProf.trim()) return;
    await api.post("/superadmin/professions", { name: newProf.trim() });
    setNewProf("");
    refresh();
  };
  const delProf = async (id) => {
    await api.delete(`/superadmin/professions/${id}`);
    refresh();
  };

  const refreshStages = () => {
    api.get("/construction-stages?include_inactive=true").then(({ data }) => setStages(data));
  };

  const addStage = async () => {
    const name = newStage.name.trim();
    if (!name || newStage.sort_order === "") return;
    try {
      await api.post("/superadmin/construction-stages",
                     { name, sort_order: Number(newStage.sort_order) });
      setNewStage({ name: "", sort_order: "" });
      refreshStages();
    } catch (err) { window.alert(err.response?.data?.detail || err.message); }
  };

  const setStageActive = async (s, active) => {
    try {
      if (active) await api.put(`/superadmin/construction-stages/${s.id}`, { active: true });
      else await api.delete(`/superadmin/construction-stages/${s.id}`);
      refreshStages();
    } catch (err) { window.alert(err.response?.data?.detail || err.message); }
  };

  const reorderStage = async (s, sort_order) => {
    try {
      await api.put(`/superadmin/construction-stages/${s.id}`, { sort_order: Number(sort_order) });
      refreshStages();
    } catch (err) { window.alert(err.response?.data?.detail || err.message); }
  };

  if (!pricing) return null;

  return (
    <div className="bg-slate-50/40 min-h-screen py-8">
      <div className="nc-container max-w-4xl space-y-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-semibold mb-2">Pricing model</h3>
          <p className="text-sm text-slate-600 mb-4">Per-PDF pricing is set on each plan's files. GST {pricing.gst_percent ?? 18}% applies on all paid items. Edit individual plan prices in the Plans tab.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(pricing.by_file_type || {}).map(([k, v]) => (
              <div key={k} className="border border-slate-200 rounded-xl p-3">
                <div className="text-xs uppercase tracking-wider text-slate-500">{k.replace("_", " ")}</div>
                <div className="text-lg font-bold text-slate-900 mt-1">
                  {v.min_paise === v.max_paise
                    ? (v.min_paise === 0 ? "Free" : `₹${Math.floor(v.min_paise / 100)}`)
                    : `₹${Math.floor(v.min_paise / 100)} – ₹${Math.floor(v.max_paise / 100)}`}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-semibold mb-4">Feature flags</h3>
          <label className="flex items-center gap-2 mb-2"><input type="checkbox" checked={!!flags.gupshup_otp} onChange={e => setFlags({ ...flags, gupshup_otp: e.target.checked })} />Gupshup OTP (WhatsApp)</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!flags.gupshup_notifications} onChange={e => setFlags({ ...flags, gupshup_notifications: e.target.checked })} />Gupshup notifications</label>
          <button onClick={saveFlags} className="nc-btn-primary mt-4" data-testid="save-flags-btn">Save flags</button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-semibold mb-4">Professions</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {profOptions.map(p => (
              <span key={p.id} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-sm">
                {p.name} <button onClick={() => delProf(p.id)} className="text-rose-500">×</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input className="nc-input flex-1" placeholder="New profession" value={newProf} onChange={e => setNewProf(e.target.value)} data-testid="new-prof-input" />
            <button onClick={addProf} className="nc-btn-primary" data-testid="add-prof-btn">Add</button>
          </div>

          <section className="mt-6" data-testid="stages-section">
            <h3 className="font-semibold text-slate-900">Construction stages</h3>
            <p className="text-sm text-slate-500 mt-1">
              What contractors report against. Numbers are spaced by ten so a new stage
              slots between two existing ones. Retiring a stage hides it from
              contractors without breaking past updates.
            </p>
            <div className="mt-3 space-y-1">
              {stages.map(s => (
                <div key={`${s.id}-${s.sort_order}`} className="flex items-center gap-3 text-sm" data-testid={`stage-row-${s.id}`}>
                  <input type="number" defaultValue={s.sort_order} className="nc-input py-1 w-20"
                         onBlur={e => {
                           const raw = e.target.value;
                           const n = Number(raw);
                           if (raw === "" || !Number.isFinite(n)) { e.target.value = s.sort_order; return; }
                           if (n === s.sort_order) return;
                           reorderStage(s, n);
                         }}
                         data-testid={`stage-order-${s.id}`} />
                  <span className={s.active ? "text-slate-900" : "text-slate-400 line-through"}>{s.name}</span>
                  <button onClick={() => setStageActive(s, !s.active)}
                          className="text-xs text-emerald-700 hover:underline"
                          data-testid={`stage-toggle-${s.id}`}>
                    {s.active ? "Retire" : "Restore"}
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 items-end">
              <label className="flex flex-col">
                <span className="text-xs text-slate-500">Order</span>
                <input type="number" className="nc-input py-2 w-24" value={newStage.sort_order}
                       onChange={e => setNewStage(s => ({ ...s, sort_order: e.target.value }))}
                       data-testid="stage-new-order" />
              </label>
              <label className="flex flex-col flex-1 min-w-[200px]">
                <span className="text-xs text-slate-500">New stage</span>
                <input className="nc-input py-2" value={newStage.name}
                       onChange={e => setNewStage(s => ({ ...s, name: e.target.value }))}
                       data-testid="stage-new-name" />
              </label>
              <button onClick={addStage} className="nc-btn-primary py-2" data-testid="stage-add">Add</button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/superadmin/audit-logs").then(({ data }) => setLogs(data)); }, []);
  return (
    <div className="bg-slate-50/40 min-h-screen py-8">
      <div className="nc-container">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Time</Th><Th>User</Th><Th>Action</Th><Th>Entity</Th><Th>Changes</Th></tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id} className="border-b border-slate-100">
                  <Td>{new Date(l.timestamp).toLocaleString()}</Td>
                  <Td className="font-mono text-xs">{l.user_id?.slice(0, 8)}</Td>
                  <Td>{l.action}</Td>
                  <Td>{l.entity}/{l.entity_id}</Td>
                  <Td className="font-mono text-xs">{JSON.stringify(l.changes).slice(0, 80)}</Td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">No audit entries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;

function EmailLogsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ email: "", status: "", from_date: "", to_date: "" });
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const limit = 50;

  const fetchLogs = async (resetPage = false) => {
    setLoading(true);
    const nextPage = resetPage ? 0 : page;
    if (resetPage) setPage(0);
    try {
      const params = { limit, offset: nextPage * limit };
      if (filters.email) params.email = filters.email;
      if (filters.status) params.status = filters.status;
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      const { data } = await api.get("/superadmin/email-logs", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); /* eslint-disable-next-line */ }, [page]);

  const onApply = (e) => { e.preventDefault(); fetchLogs(true); };
  const onClear = () => {
    setFilters({ email: "", status: "", from_date: "", to_date: "" });
    setPage(0);
    setTimeout(() => fetchLogs(true), 0);
  };

  const onDelete = async () => {
    const hasFilter = filters.email || filters.status || filters.from_date || filters.to_date;
    if (!hasFilter) {
      alert("Set at least one filter before deleting.");
      return;
    }
    const summary = [
      filters.status && `status=${filters.status}`,
      filters.email && `email contains "${filters.email}"`,
      filters.from_date && `from ${filters.from_date}`,
      filters.to_date && `to ${filters.to_date}`,
    ].filter(Boolean).join(", ");
    if (!window.confirm(`Delete ${total.toLocaleString()} email log(s) matching: ${summary}?\n\nThis cannot be undone.`)) return;
    const params = {};
    if (filters.email) params.email = filters.email;
    if (filters.status) params.status = filters.status;
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;
    try {
      const { data } = await api.delete("/superadmin/email-logs", { params });
      alert(`Deleted ${data.deleted} log(s).`);
      setPage(0);
      fetchLogs(true);
    } catch (e) {
      alert(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const statusBadge = (s) => {
    const map = {
      sent: "bg-emerald-100 text-emerald-700",
      failed: "bg-rose-100 text-rose-700",
      mock: "bg-amber-100 text-amber-700",
      skipped: "bg-slate-100 text-slate-600",
    };
    return <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${map[s] || "bg-slate-100 text-slate-600"}`}>{s}</span>;
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="bg-slate-50/40 min-h-screen py-8">
      <div className="nc-container space-y-4">
        <form onSubmit={onApply} className="bg-white rounded-2xl border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email contains</label>
            <input
              className="nc-input mt-1 w-full"
              placeholder="user@example.com"
              value={filters.email}
              onChange={(e) => setFilters({ ...filters, email: e.target.value })}
              data-testid="email-logs-filter-email"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</label>
            <select
              className="nc-input mt-1 w-full"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              data-testid="email-logs-filter-status"
            >
              <option value="">All</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="mock">Mock</option>
              <option value="skipped">Skipped</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">From date</label>
            <input
              type="date"
              className="nc-input mt-1 w-full"
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
              data-testid="email-logs-filter-from"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">To date</label>
            <input
              type="date"
              className="nc-input mt-1 w-full"
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
              data-testid="email-logs-filter-to"
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="nc-btn-primary flex-1" data-testid="email-logs-apply">Apply</button>
            <button type="button" onClick={onClear} className="nc-btn-secondary flex-1">Clear</button>
          </div>
          <div className="md:col-span-5 flex justify-end">
            <button
              type="button"
              onClick={onDelete}
              className="text-sm px-3 py-1.5 rounded-lg bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 disabled:opacity-50"
              disabled={total === 0}
              data-testid="email-logs-delete"
            >Delete {total.toLocaleString()} matching</button>
          </div>
        </form>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Time</Th><Th>To</Th><Th>Subject</Th><Th>Status</Th><Th>Channel</Th><Th>Provider ID</Th><Th></Th></tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-slate-100">
                  <Td>{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</Td>
                  <Td className="font-mono text-xs">{r.to}</Td>
                  <Td>{r.subject}</Td>
                  <Td>{statusBadge(r.status)}</Td>
                  <Td className="text-xs text-slate-500">{r.channel || "—"}</Td>
                  <Td className="font-mono text-xs text-slate-500">{r.provider_id ? r.provider_id.slice(0, 12) : "—"}</Td>
                  <Td>
                    <button
                      onClick={() => setSelected(r)}
                      className="text-blue-600 hover:underline text-xs"
                      data-testid={`email-logs-view-${r.id}`}
                    >View</button>
                  </Td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-slate-400">No emails match these filters.</td></tr>
              )}
              {loading && (
                <tr><td colSpan={7} className="p-6 text-center text-slate-400">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between text-sm text-slate-600">
          <div>{total.toLocaleString()} total · page {page + 1} of {totalPages}</div>
          <div className="flex gap-2">
            <button
              disabled={page === 0}
              onClick={() => setPage(Math.max(0, page - 1))}
              className="nc-btn-secondary disabled:opacity-50"
            >Previous</button>
            <button
              disabled={page + 1 >= totalPages}
              onClick={() => setPage(page + 1)}
              className="nc-btn-secondary disabled:opacity-50"
            >Next</button>
          </div>
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-semibold text-lg">{selected.subject}</h3>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600 text-xl">×</button>
            </div>
            <dl className="grid grid-cols-3 gap-2 text-sm mb-4">
              <dt className="text-slate-500">To</dt><dd className="col-span-2 font-mono text-xs break-all">{selected.to}</dd>
              <dt className="text-slate-500">Status</dt><dd className="col-span-2">{statusBadge(selected.status)}</dd>
              <dt className="text-slate-500">Channel</dt><dd className="col-span-2">{selected.channel || "—"}</dd>
              <dt className="text-slate-500">Provider ID</dt><dd className="col-span-2 font-mono text-xs break-all">{selected.provider_id || "—"}</dd>
              <dt className="text-slate-500">Reply-To</dt><dd className="col-span-2 font-mono text-xs break-all">{selected.reply_to || "—"}</dd>
              <dt className="text-slate-500">Sent at</dt><dd className="col-span-2">{selected.created_at ? new Date(selected.created_at).toLocaleString() : ""}</dd>
              {selected.error && (<><dt className="text-slate-500">Error</dt><dd className="col-span-2 text-rose-600 text-xs whitespace-pre-wrap">{selected.error}</dd></>)}
            </dl>
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Body preview</div>
              <pre className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs whitespace-pre-wrap break-words max-h-96 overflow-y-auto">{selected.body_text || "(empty)"}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
